import { Button, notify } from '@affine/component';
import { FetchService } from '@affine/core/modules/cloud';
import { IntegrationService } from '@affine/core/modules/integration';
import { UrlService } from '@affine/core/modules/url';
import { GithubIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { IntegrationSettingHeader } from '../setting';
import * as styles from './setting-panel.css';

export const GitHubSettingPanel = () => {
  const github = useService(IntegrationService).github;
  const settings = useLiveData(github.settings$);
  const accessToken = settings?.accessToken;

  return accessToken ? (
    <GitHubConnectedSetting />
  ) : (
    <GitHubNotConnectedSetting />
  );
};

const GitHubSettingHeader = ({ action }: { action?: React.ReactNode }) => {
  return (
    <IntegrationSettingHeader
      icon={<GithubIcon />}
      name="GitHub"
      desc="Import and sync markdown files with your GitHub repositories."
      action={action}
    />
  );
};

const GitHubNotConnectedSetting = () => {
  const github = useService(IntegrationService).github;
  const fetchService = useService(FetchService);
  const urlService = useService(UrlService);
  const [loading, setLoading] = useState(false);

  // Handle OAuth callback data (from postMessage or localStorage)
  const handleOAuthCallback = useCallback(
    async (data: { type: string; accessToken?: string; error?: string }) => {
      if (data.type === 'github-oauth-success' && data.accessToken) {
        try {
          // Verify token and get user info
          const response = await fetchService.fetch('/api/github/me', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accessToken: data.accessToken }),
          });

          if (!response.ok) {
            throw new Error('Failed to verify GitHub token');
          }

          const result = await response.json();
          github.connect(data.accessToken, result.user);
          notify.success({
            title: 'Connected to GitHub',
            message: `Signed in as ${result.user.name || result.user.login}`,
          });
        } catch (error) {
          notify.error({
            title: 'Failed to connect',
            message:
              error instanceof Error ? error.message : 'Please try again',
          });
        }
        setLoading(false);
      } else if (data.type === 'github-oauth-error') {
        notify.error({
          title: 'Failed to connect',
          message: data.error || 'Authentication failed',
        });
        setLoading(false);
      }
    },
    [fetchService, github]
  );

  // Listen for OAuth callback via postMessage
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'github-oauth-success' ||
        event.data?.type === 'github-oauth-error'
      ) {
        handleOAuthCallback(event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleOAuthCallback]);

  // Listen for OAuth callback via localStorage (fallback when window.opener is null)
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'github-oauth-callback' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          // Clear the storage item to prevent re-processing
          localStorage.removeItem('github-oauth-callback');
          handleOAuthCallback(data);
        } catch (e) {
          console.error('Failed to parse OAuth callback data:', e);
        }
      }
    };

    window.addEventListener('storage', handleStorage);

    // Also check if there's already data in localStorage (in case we missed the event)
    const existingData = localStorage.getItem('github-oauth-callback');
    if (existingData && loading) {
      try {
        const data = JSON.parse(existingData);
        // Only process if it's recent (within last 30 seconds)
        if (data.timestamp && Date.now() - data.timestamp < 30000) {
          localStorage.removeItem('github-oauth-callback');
          handleOAuthCallback(data);
        } else {
          // Clear stale data
          localStorage.removeItem('github-oauth-callback');
        }
      } catch (e) {
        localStorage.removeItem('github-oauth-callback');
      }
    }

    return () => window.removeEventListener('storage', handleStorage);
  }, [handleOAuthCallback, loading]);

  const handleConnect = useCallback(async () => {
    setLoading(true);
    try {
      // Get the authorization URL from the backend
      const response = await fetchService.fetch('/api/github/oauth/authorize', {
        method: 'GET',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          error.message || 'Failed to start GitHub authentication'
        );
      }

      const { authUrl } = await response.json();

      // Open popup window for OAuth
      urlService.openPopupWindow(authUrl);
    } catch (error) {
      notify.error({
        title: 'Failed to connect',
        message:
          error instanceof Error
            ? error.message
            : 'Please check your configuration and try again',
      });
      setLoading(false);
    }
  }, [fetchService, urlService]);

  // Check if GitHub integration is configured on the server
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetchService.fetch('/api/github/status');
        if (response.ok) {
          const data = await response.json();
          setIsConfigured(data.configured);
        }
      } catch {
        setIsConfigured(false);
      }
    };
    checkStatus();
  }, [fetchService]);

  if (isConfigured === false) {
    return (
      <div>
        <GitHubSettingHeader />
        <div className={styles.connectSection}>
          <div className={styles.hint}>
            GitHub integration is not configured on this server. Please contact
            your administrator to set up GitHub OAuth credentials.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <GitHubSettingHeader />
      <div className={styles.connectSection}>
        <div className={styles.hint}>
          Connect your GitHub account to import and sync markdown files from
          your repositories.
        </div>
        <Button
          variant="primary"
          size="large"
          onClick={handleConnect}
          disabled={loading || isConfigured === null}
          className={styles.connectButton}
          prefix={<GithubIcon />}
        >
          {loading ? 'Connecting...' : 'Connect with GitHub'}
        </Button>
      </div>
    </div>
  );
};

const GitHubConnectedSetting = () => {
  const github = useService(IntegrationService).github;
  const settings = useLiveData(github.settings$);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = useCallback(() => {
    setDisconnecting(true);
    github.disconnect();
    notify.success({ title: 'Disconnected from GitHub' });
    setDisconnecting(false);
  }, [github]);

  return (
    <div>
      <GitHubSettingHeader
        action={
          <Button
            variant="error"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            Disconnect
          </Button>
        }
      />
      <div className={styles.connectedSection}>
        {settings?.avatarUrl && settings?.username && (
          <div className={styles.userInfo}>
            <img
              src={settings.avatarUrl}
              alt={settings.username}
              className={styles.userAvatar}
            />
            <div>
              <div className={styles.userName}>{settings.username}</div>
              <div className={styles.userLogin}>@{settings.username}</div>
            </div>
          </div>
        )}
        <div className={styles.connectedMessage}>
          Your GitHub account is connected. You can now import markdown files
          from your repositories and sync changes back to GitHub.
        </div>
      </div>
    </div>
  );
};
